/**
 * Common primitives and base types for the OWA Extensibility SDK
 */

import type { MailboxInfo } from 'owa-client-types';

/**
 * Unique identifier for a compose session
 */
export type ComposeId = string;

/**
 * OWA module names
 */
export type ModuleName = 'Mail' | 'Calendar';

/**
 * Compose operation modes
 */
export type ComposeMode = 'New' | 'Reply' | 'ReplyAll' | 'Forward' | 'Draft';

/**
 * Reasons for compose window closure
 */
export type ComposeCloseReason =
    | 'UserDiscard'
    | 'SendSuccess'
    | 'SendFailure'
    | 'WindowClose'
    | 'TabSwitch'
    | 'Unknown';

/**
 * Telemetry context for SDK operations
 * Contains correlation-friendly identifiers that are safe for telemetry
 */
export interface ISdkTelemetryContext {
    /** Correlation ID for stitching logs across related operations */
    correlationId: string;
    /** Timestamp in milliseconds */
    timestampMs: number;
}

/**
 * Mandatory context envelope for all SDK events and operations
 * Contains correlation + mailbox scope required for capability checks
 */
export interface ISdkContext {
    /** Mailbox information for scoping operations */
    mailboxInfo: MailboxInfo;
    /** Telemetry context for correlation */
    telemetry: ISdkTelemetryContext;
}

/**
 * Recipient field types
 */
export type RecipientField = 'To' | 'Cc' | 'Bcc' | 'Other';

/**
 * Attachment kinds as used by OWA add-ins APIs
 *
 * Notes:
 * - `Cloud` corresponds to a ReferenceAttachment
 * - `Base64` is used for flows where content is provided as base64
 * - Attachment lists/events typically surface the resolved kind (usually `File`)
 */
export type AttachmentKind = 'File' | 'Item' | 'Cloud' | 'Base64';

/**
 * Body format types
 */
export type BodyFormat = 'Text' | 'Html';

/**
 * Format of attachment content returned by getAttachmentContent
 */
export type AttachmentContentFormat = 'base64' | 'eml' | 'url' | 'iCalendar';

/**
 * Body mode for compose scenarios.
 * Controls whether quoted body content is included in the response.
 *
 * - FullBody: Always include the quoted body (e.g. reply/forward content)
 * - HostConfig: Include quoted body based on host configuration (e.g. only in full compose, not inline)
 */
export enum BodyMode {
    FullBody = 0,
    HostConfig = 1,
}

/**
 * SDK error codes
 */
export type SdkErrorCode =
    | 'ItemNotFound'
    | 'OperationNotSupported'
    | 'OperationDenied'
    | 'Unknown'
    | 'URLNotFound'
    | 'Base64NotFound'
    | 'ItemIdNotFound';

/**
 * SDK error contract
 */
export interface ISdkError {
    /** Error code category */
    code: SdkErrorCode;
    /** Optional error message */
    message?: string;
}

/**
 * Standard SDK operation result
 */
export interface ISdkOperationResult {
    /** Whether the operation succeeded */
    ok: boolean;
    /** Error details if operation failed */
    error?: ISdkError;
}

/**
 * Item identifier
 */
export type ItemId = string;

/**
 * Conversation identifier
 */
export type ConversationId = string;

/**
 * Email recipient type
 */
export type RecipientType = 'Other' | 'DistributionList' | 'User' | 'ExternalUser';

/**
 * Email address contract
 */
export interface IEmailAddress {
    /** Display name */
    name: string;
    /** Email address */
    address: string;
    /** Recipient type (optional) */
    recipientType?: RecipientType;
}

/**
 * Notification level for compose notifications
 */
export type ComposeNotificationLevel = 'Info' | 'Warning' | 'Error';

/**
 * Source of selected data for get/set operations
 */
export type SelectedDataSource = 'subject' | 'body';

/**
 * Selected data contract for getSelectedData/setSelectedData operations
 * Represents a contiguous selection of text from a specific source (e.g. body or subject)
 * along with metadata about the selection range and source.
 */
export interface SelectedData {
    /** Selected data */
    data: string;
    /** Source property of the selected data (e.g. body, subject) */
    sourceProperty: SelectedDataSource;
    /** Start position of the selection (for body or subject selected data) */
    startPosition: number;
    /** End position of the selection (for body or subject selected data) */
    endPosition: number;
}

/**
 * Internet headers for an email message
 */
export interface InternetHeaders {
    /**
     * Key-value pairs representing the internet headers of the email message
     */
    [name: string]: string;
}
